import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { doGet2 } from '../../services/common'

const PAGE_SIZE = 1000

export type PromoItem = {
  id: number
  imageUrl?: string
  expiryDate: string
  title?: string
  copy?: string
  cta?: string
  ctaUrl?: string
  abbreviatedTerms: string
}

type PromoPageStepResponse = {
  content?: Array<{
    id: number
    imageUrl?: string
    end?: string
    headline?: string
    copy?: string
    ctaButtonCopy?: string
    ctaLink?: string
    abbreviatedTerms?: string
  }>
}

/*
  Loads the promo offers attached to a single beaker2 step (BR-5419). The
  site-scoped equivalent lives in fifaunilever-site and hits /promo-page.
*/
export const usePromoItems = (
  stepId?: number | string,
  params: Record<string, string> = {},
) => {
  const [promoItems, setPromoItems] = useState<PromoItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Callers pass a fresh object literal each render; serializing keeps this
  // effect from refetching forever.
  const paramsKey = JSON.stringify(params)

  useEffect(() => {
    if (!stepId) {
      setPromoItems([])
      setIsLoading(false)
      return undefined
    }

    let cancelled = false

    const loadItems = async () => {
      setIsLoading(true)
      try {
        const queryParams = new URLSearchParams({
          stepId: String(stepId),
          page: '0',
          size: String(PAGE_SIZE),
          ...(JSON.parse(paramsKey) as Record<string, string>),
        })

        const response = await doGet2(
          `/promo-page-step?${queryParams.toString()}`,
          {},
        )
        const result: PromoPageStepResponse = await response.json()

        if (cancelled) return

        setPromoItems(
          (result.content ?? []).map(item => ({
            id: item.id,
            imageUrl: item.imageUrl,
            expiryDate: item.end ? dayjs(item.end).format('MM/DD/YY') : '',
            title: item.headline,
            copy: item.copy,
            cta: item.ctaButtonCopy,
            ctaUrl: item.ctaLink,
            abbreviatedTerms: item.abbreviatedTerms || '',
          })),
        )
        setError(null)
      } catch (e) {
        if (cancelled) return
        console.warn('[OffersSection] Error loading offers:', e)
        setError('Failed to load offers')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadItems()

    return () => {
      cancelled = true
    }
  }, [stepId, paramsKey])

  return { promoItems, isLoading, error }
}
